import { AggregateCartesianChartDataConfig } from '@app/data/interface/widget-config';
import { SeriesOption } from 'echarts';
import moment from 'moment';
import { color } from '@app/client/clients/wastemanagement/taiao-dashboard-layout/colors';
import { EChartsOption } from 'echarts';
import { dataViewFormatter } from '@app/widgets/helpers/widgets';
import {
  buildAllowedIdentifierSet,
  buildSourceIdToIdentifierMap,
  resolveNormalizedSourceIdentifier,
} from './source-filter-utils';

interface BirdObservation {
  date: string;
  species: string;
  heard: number;
  sawAndHeard: number;
  zeroCount: number;
}

const parseMetadata = (metadata: any): { evidence: string } | null => {
  if (!metadata) return null;
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      return typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  }
  if (typeof metadata === 'object') {
    return metadata;
  }
  return null;
};

const lightenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;

  const toHex = (c: number) => `00${c.toString(16)}`.slice(-2);

  return `#${toHex(R < 255 ? (R < 1 ? 0 : R) : 255)}${toHex(G < 255 ? (G < 1 ? 0 : G) : 255)}${toHex(
    B < 255 ? (B < 1 ? 0 : B) : 255
  )}`;
};

/**
 * Maps wetlands bird species observation data to a format suitable for the chart.
 * @param _keyedBySourceId The data keyed by source ID.
 * @param _idToLabelMap A map of entity IDs to their labels.
 * @param xAxisData The X-axis data for the chart.
 * @param legendLabels The legend labels for the chart.
 * @param dataSource The data source for the chart.
 * @param seriesData The series data for the chart.
 * @returns The mapped series data for the chart.
 */
export const wetlandsBirdSpeciesMapper: AggregateCartesianChartDataConfig['mapDataToSeries'] = (
  _keyedBySourceId,
  _idToLabelMap,
  xAxisData,
  legendLabels,
  dataSource,
  seriesData,
  selectedSources
) => {
  const observations: {
    [treatment: string]: { [species: string]: { heard: number; sawAndHeard: number; zeroCount: number } };
  } = {};

  const entityArray: any[] = Array.isArray(seriesData?.entities) ? seriesData!.entities : [];
  const entityLookup = entityArray.reduce((acc: any, entity: any) => {
    acc[entity.id] = entity.name;
    return acc;
  }, {});

  const sourceInfoMap = new Map<string, { treatment: string }>();
  if (seriesData?.environmentalDataSources) {
    seriesData.environmentalDataSources.forEach((source: any) => {
      if (source.id) {
        sourceInfoMap.set(String(source.id), {
          treatment: source.source_metadata?.Treatment || 'Unknown',
        });
      }
    });
  }

  const allowedIdentifiers = buildAllowedIdentifierSet(selectedSources);
  const sourceIdToIdentifier = buildSourceIdToIdentifierMap(seriesData);

  const dataPoints: any[] = Array.isArray(seriesData?.data) ? seriesData.data : [];

  dataPoints.forEach((item: any) => {
    const normalizedIdentifier = resolveNormalizedSourceIdentifier(item, sourceIdToIdentifier);
    if (allowedIdentifiers && (!normalizedIdentifier || !allowedIdentifiers.has(normalizedIdentifier))) {
      return;
    }

    const sourceId = String(item.environmental_data_source_id);
    const sourceInfo = sourceInfoMap.get(sourceId);
    const treatment = sourceInfo?.treatment || 'Unknown';

    const speciesId = item.observation_entity_id;
    const species = entityLookup[speciesId] || 'Unknown Species';
    const metadata = parseMetadata(item.collection_metadata);
    const evidence = metadata?.evidence?.toLowerCase();
    const value = Number(item.avg) || 0;

    if (!observations[treatment]) {
      observations[treatment] = {};
    }
    if (!observations[treatment][species]) {
      observations[treatment][species] = { heard: 0, sawAndHeard: 0, zeroCount: 0 };
    }

    if (value > 0) {
      if (evidence === 'heard') {
        observations[treatment][species].heard += value;
      } else if (evidence === 'saw-and-heard') {
        observations[treatment][species].sawAndHeard += value;
      }
    } else {
      observations[treatment][species].zeroCount += 1;
    }
  });

  const treatments = Object.keys(observations).sort();
  xAxisData.length = 0;
  xAxisData.push(...treatments);

  const allSpecies = [
    ...new Set(
      Object.values(observations).reduce((acc: string[], obs: any) => {
        return acc.concat(Object.keys(obs));
      }, [])
    ),
  ].sort();
  const series: (SeriesOption & { dataViewObservations?: BirdObservation[] })[] = [];
  legendLabels.length = 0;
  legendLabels.push(...allSpecies);
  if (!legendLabels.includes('No Birds Observed')) {
    legendLabels.push('No Birds Observed');
  }

  allSpecies.forEach((s, i) => {
    const baseColor = color[i % color.length];
    const lighterColor = lightenColor(baseColor, 20); // 20% lighter

    series.push({
      name: s,
      type: 'bar',
      stack: s,
      legendHoverLink: false,
      data: treatments.map((treatment) => observations[treatment]?.[s]?.heard || 0),
      itemStyle: {
        color: lighterColor,
      },
      tooltip: {
        valueFormatter: (value) => `${value} (Heard)`,
      },
    });
    series.push({
      name: s,
      type: 'bar',
      stack: s,
      legendHoverLink: false,
      data: treatments.map((treatment) => observations[treatment]?.[s]?.sawAndHeard || 0),
      itemStyle: {
        color: baseColor,
      },
      tooltip: {
        valueFormatter: (value) => `${value} (Saw and Heard)`,
      },
    });
  });

  allSpecies.forEach((s, i) => {
    const baseColor = color[i % color.length];
    series.push({
      name: 'No Birds Observed',
      type: 'bar',
      stack: s,
      legendHoverLink: true,
      itemStyle: {
        color: 'transparent',
        borderColor: baseColor,
        borderWidth: 2,
      },
      data: treatments.map((treatment, index) => {
        const zeroCount = observations[treatment]?.[s]?.zeroCount || 0;
        return [index, zeroCount > 0 ? zeroCount : 0];
      }),
      z: 10,
    });
  });

  return series;
};

/**
 * Converts wetland bird species chart data into a well-formatted data view table.
 *
 * @param option The ECharts option to convert.
 * @returns A string or HTMLElement representing the content.
 */
export const formatWetlandBirdSpeciesDV = (option: EChartsOption): string | HTMLElement => {
  const seriesArray = Array.isArray(option.series) ? option.series : [];
  if (!seriesArray.length) {
    return dataViewFormatter(option);
  }

  const rebuiltSeries: SeriesOption[] = seriesArray.map((s: any) => {
    if (typeof s !== 'object' || !s.stack) {
      return s;
    }
    const species = s.stack;
    let seriesName = species;

    // Distinguish between 'Heard' and 'Saw and Heard' for series that share the same name as the species
    if (s.name === species) {
      if (s.tooltip?.valueFormatter?.(1).includes('Heard')) {
        seriesName = `${species} (Heard)`;
      } else if (s.tooltip?.valueFormatter?.(1).includes('Saw and Heard')) {
        seriesName = `${species} (Saw and Heard)`;
      }
    } else if (s.name === 'No Birds Observed') {
      seriesName = `${species} (No Birds Observed)`;

      // the series data for 'No Birds Observed' is in [index, value] format, we need to extract the value
      const newData = (s.data as any[]).map((dataPoint) => {
        if (Array.isArray(dataPoint) && dataPoint.length === 2) {
          return dataPoint[1];
        }
        return dataPoint;
      });
      return {
        ...s,
        name: seriesName,
        data: newData,
      };
    } else {
      seriesName = s.name; // Fallback
    }

    return {
      ...s,
      name: seriesName,
    };
  });

  const rebuiltOption: EChartsOption = {
    ...option,
    series: rebuiltSeries,
  };

  return dataViewFormatter(rebuiltOption);
};
